import { AxiosError } from "axios"
import { ApiError, Utils } from "rp-components"
import {
    WALLET_PASS_STATUS_GROUPS,
    WALLET_PASS_STATUS_VARIANTS,
    WALLET_TYPE_LABELS,
    WalletErrorCode,
    WalletPass,
    WalletPassStatus,
    WalletPassStatusGroup,
    WalletType
} from "@/models/Wallet"
import { GOOGLE_WALLET_PROVISIONING_SOURCE, GOOGLE_WALLET_SAVE_URL, WALLET_MESSAGES } from "@/utils/WalletConstants"

const HTTP_BAD_REQUEST = 400
const HTTP_CONFLICT = 409
const UNKNOWN_STATUS_VARIANT = "secondary"
const WALLET_TYPES = new Set<string>(Object.values(WalletType))
const FRACTIONAL_SECONDS = /\.(\d+)/
const HAS_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i
const MILLISECOND_DIGITS = 3

const MESSAGES_BY_ERROR_CODE: Record<WalletErrorCode, string> = {
    [WalletErrorCode.ISSUANCE_TOKEN_EXPIRED]: WALLET_MESSAGES.TOKEN_EXPIRED,
    [WalletErrorCode.CARD_IN_TOO_MANY_WALLETS]: WALLET_MESSAGES.TOO_MANY_WALLETS,
    [WalletErrorCode.UN_SUPPORTED_REQUEST]: WALLET_MESSAGES.UNSUPPORTED_DEVICE,
    [WalletErrorCode.INVALID_ISSUANCE_TOKEN]: WALLET_MESSAGES.ALREADY_ADDED
}

const MESSAGES_BY_HTTP_STATUS: Record<number, string> = {
    [HTTP_CONFLICT]: WALLET_MESSAGES.ALREADY_ADDED,
    [HTTP_BAD_REQUEST]: WALLET_MESSAGES.UNSUPPORTED_DEVICE
}

interface WalletApiError extends ApiError {
    hidErrorCode?: string
}

export function googleWalletSaveUrl(blob: string): string {
    return `${GOOGLE_WALLET_SAVE_URL}/${blob}?provisioning_source=${GOOGLE_WALLET_PROVISIONING_SOURCE}`
}

export function walletErrorMessage(error: unknown): string {
    const response = (error as AxiosError | undefined)?.response
    const hidErrorCode = (response?.data as WalletApiError | undefined)?.hidErrorCode
    const byCode = hidErrorCode ? MESSAGES_BY_ERROR_CODE[hidErrorCode as WalletErrorCode] : undefined
    const byStatus = response?.status ? MESSAGES_BY_HTTP_STATUS[response.status] : undefined
    return byCode ?? byStatus ?? WALLET_MESSAGES.GENERIC_ERROR
}

export function walletPassStatusGroup(status?: string): WalletPassStatusGroup | undefined {
    return status ? WALLET_PASS_STATUS_GROUPS[status as WalletPassStatus] : undefined
}

export function walletPassStatusLabel(status?: string): string {
    return walletPassStatusGroup(status) ?? Utils.formatEnumValue(status ?? "")
}

export function walletPassStatusVariant(status?: string): string {
    const group = walletPassStatusGroup(status)
    return group ? WALLET_PASS_STATUS_VARIANTS[group] : UNKNOWN_STATUS_VARIANT
}

export function hidDateTimeToEpoch(value?: string | null): number | undefined {
    if (!value || !HAS_TIME_ZONE.test(value)) {
        return undefined
    }
    const normalized = value.replace(
        FRACTIONAL_SECONDS,
        (_match, fraction: string) => `.${fraction.padEnd(MILLISECOND_DIGITS, "0").slice(0, MILLISECOND_DIGITS)}`
    )
    const epoch = Date.parse(normalized)
    return isNaN(epoch) ? undefined : epoch
}

export function isWalletType(value: string): value is WalletType {
    return WALLET_TYPES.has(value)
}

export function isWalletPass(pass: WalletPass): boolean {
    return !pass.method || isWalletType(pass.method)
}

export function isWalletPassExpired(pass: WalletPass, now: number = Date.now()): boolean {
    const expiresAt = hidDateTimeToEpoch(pass.expiresDateTime)
    return pass.status === WalletPassStatus.CREATED && expiresAt !== undefined && expiresAt < now
}

export function isWalletPassRedeemable(pass: WalletPass, now: number = Date.now()): boolean {
    return isWalletPass(pass) && pass.status === WalletPassStatus.CREATED && !isWalletPassExpired(pass, now)
}

export function walletPassPlatformLabel(pass: WalletPass): string | undefined {
    const platform = pass.device?.platform ?? pass.credentials.find((credential) => credential.platformType)?.platformType
    return platform && isWalletType(platform) ? WALLET_TYPE_LABELS[platform] : undefined
}

export function walletPassDeviceName(pass: WalletPass): string | undefined {
    const deviceType = pass.device?.deviceType ?? pass.deviceType
    return pass.device?.name ?? (deviceType ? Utils.formatEnumValue(deviceType) : undefined)
}
